import { useEffect, useRef } from "react";

import { useSelector, useStore } from "react-redux";

import type { FiltersConfig } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import type { SearchFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/searchFilterInstance";
import type { RootState } from "single-spa/store/types";

import { selectDirtyFiltersFlag } from "../store/selectors";
import type { LeadsSearchAllFiltersTypes } from "../types/leadsSearchFilters.dto";
import { EMPTY_INITIAL_SEED, type InitialLeadsSeed } from "../utils/initialSeed";

import { useFiltersUpdate } from "./useFiltersUpdate";

/**
 * Writes the surface's initial filter values, once, when the panel has none set. Runs at the **surface root**
 * (not the per-tab panel), so `seed` is a render value at the same level as this reader and composes into a
 * single write with no cross-component effect ordering.
 *
 * The value is each filter's own default (`getInitialDtoValue`) merged under the route `seed`, so the seed
 * (i.e. a URL deep link) wins on any shared key. The seed is the surfaces' intent, not a filter's concern — carried
 * in explicitly rather than read off a built model, so a seeded value is written even for a filter that has
 * not built yet; the filter picks it up from redux when it does. Surfaces with no seed pass none and get defaults only.
 *
 * Re-seeds every time state goes empty (e.g. "clear all filters"). Defaults are the base state and a seed is a
 * one-shot mask over it: each seed is applied once, so clearing lands on the defaults rather than replaying the
 * deep link. A URL change hands over a new seed (the params are dynamic, so the page does not remount) and that
 * one is applied in turn. Race safety: the decision reads `store.getState()` synchronously, so a dispatch that
 * landed in the same render tick is observed before we decide to seed.
 */
export const useApplyInitialFilters = (
    { filtersList, status }: FiltersConfig<readonly SearchFilterInstance[]>,
    seed: InitialLeadsSeed = EMPTY_INITIAL_SEED,
): void => {
    const store = useStore<RootState>();

    const hasFilters = useSelector(selectDirtyFiltersFlag);

    const onFiltersUpdate = useFiltersUpdate();

    const appliedSeedRef = useRef<InitialLeadsSeed | null>(null);

    useEffect(() => {
        if (status === "pending") {
            return;
        }

        if (!filtersList || filtersList.length === 0) {
            return;
        }

        if (selectDirtyFiltersFlag(store.getState())) {
            return;
        }

        // TODO ADR-0008: this initial-values composition relocates to advanced-search-composition.
        const defaults: Partial<LeadsSearchAllFiltersTypes> = filtersList.reduce((acum, { key, filter }) => {
            const initialDtoValue = filter.getInitialDtoValue();

            if (!initialDtoValue) {
                return acum;
            }

            return {
                ...acum,
                [key]: initialDtoValue,
            };
        }, {} as LeadsSearchAllFiltersTypes);

        const isSeedConsumed = appliedSeedRef.current === seed;
        appliedSeedRef.current = seed;

        onFiltersUpdate(isSeedConsumed ? defaults : { ...defaults, ...seed });
    }, [filtersList, status, onFiltersUpdate, hasFilters, store, seed]);
};
